import React, { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './OutputBattleTeaser.css';

const trails = [
    { level: 'N5', name: 'The First Fields', bosses: 'Skeleton Minion · Rogue · Warrior', art: 'skel-warrior' },
    { level: 'N4', name: 'Twilight Woods', bosses: 'Orc Raider · Cursed Man · Werewolf', art: 'werewolf' },
    { level: 'N3', name: 'The Misty Pass', bosses: 'Skeleton Mage · Necromancer · Bone Golem', art: 'skel-golem' },
];

export default function OutputBattleTeaser({ signupRequired = false }) {
    const [mapOpen, setMapOpen] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [inView, setInView] = useState(false);
    const sectionRef = useRef(null);
    const id = useId();
    const showMap = mapOpen || hovered;
    useEffect(() => {
        if (!window.IntersectionObserver) { setInView(true); return undefined; }
        const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting));
        observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);
    return (
        <section ref={sectionRef} aria-labelledby={`${id}-heading`} className={`battle-teaser ${inView ? 'is-in-view' : ''} max-w-screen-xl w-full mx-auto mt-6 sm:mt-10 max-sm:mt-4 overflow-hidden rounded-2xl bg-logoColor text-white shadow-blueBoxShadow`}>
            <div className="grid md:grid-cols-2">
                <div className="p-6 sm:p-8 lg:p-10 max-sm:p-5">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                        <span className="rounded-full border border-blue-200 bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">BETA</span>
                        <span className="text-sm font-semibold text-emerald-200">Free to play</span>
                    </div>
                    <h2 id={`${id}-heading`} className="text-2xl sm:text-3xl font-bold">OniKanji Output Battle</h2>
                    <p className="mt-3 text-lg max-sm:text-base font-semibold text-white">Your next sentence is your next attack.</p>
                    <p className="mt-3 text-sm sm:text-base leading-relaxed text-blue-100">
                        Put your Japanese into action. Write sentences, battle bosses, and discover loot in a 3D roguelite adventure. Practice grammar and vocabulary from N5 through N3.
                    </p>
                    <Link to={signupRequired ? '/signup' : '/output-battle'} className="mt-6 max-sm:mt-4 max-sm:w-full inline-flex items-center justify-center rounded-lg bg-white px-6 py-3 font-bold text-logoColor hover:bg-blue-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white">
                        {signupRequired ? 'Sign up free to play' : 'Play Output Battle'} <span aria-hidden="true" className="ml-2">→</span>
                    </Link>
                    <p className="mt-3 text-xs text-blue-100">Free with your OniKanji account. No subscription needed.</p>
                    {signupRequired && <Link to="/login" className="mt-3 inline-block text-sm text-blue-100 underline underline-offset-4">Already have an account? Log in</Link>}
                </div>
                <div className="battle-preview" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
                    <div className={`battle-scenery ${showMap ? 'map-showing' : ''}`} aria-hidden="true">
                        <img src="/output-battle/assets/arena-n5.webp" alt="" loading="lazy" className="battle-landscape" />
                        <div className="battle-firefly spark-one" /><div className="battle-firefly spark-two" />
                        <img src="/output-battle/assets/knight-idle.webp" alt="" loading="lazy" className="battle-fighter battle-knight" />
                        <img src="/output-battle/assets/skel-warrior-idle.webp" alt="" loading="lazy" className="battle-fighter battle-enemy" />
                        <span className="battle-scene-caption">Write a sentence. Land your next hit.</span>
                    </div>
                    <div id={`${id}-map`} className="battle-trail-map" hidden={!showMap}>
                        <p className="battle-map-heading">YOUR PATH TO THE SUMMIT</p>
                        <ol>
                            {trails.map(trail => <li key={trail.level}>
                                <span className="battle-map-node">{trail.level}</span>
                                <div><strong>{trail.name}</strong><p>{trail.bosses}</p></div>
                                <img src={`/output-battle/assets/${trail.art}-idle.webp`} alt="" loading="lazy" />
                            </li>)}
                        </ol>
                        <p className="battle-map-note">9 bosses available now · N2 &amp; N1 coming later</p>
                    </div>
                    <button type="button" className="battle-map-toggle" aria-controls={`${id}-map`} aria-expanded={showMap}
                        onClick={() => { setMapOpen(!mapOpen); setHovered(false); }}
                        onKeyDown={event => { if (event.key === 'Escape') { setMapOpen(false); setHovered(false); } }}>
                        {mapOpen ? 'Back to battleground' : hovered ? 'Keep map open' : 'Explore the map'} <span aria-hidden="true">↗</span>
                    </button>
                </div>
            </div>
        </section>
    );
}
